import { Link } from 'react-router-dom';
import SEOHead from '@/components/seo/SEOHead';
import Breadcrumbs from '@/components/seo/Breadcrumbs';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Printer, CheckCircle2, ShieldCheck, Zap, HelpCircle } from 'lucide-react';
import { 
  createServiceSchema, 
  createBreadcrumbListSchema, 
  createFAQPageSchema, 
  createSpeakable 
} from '@/utils/schemaGenerator';

export default function PricingPage() {
  const breadcrumbItems = [
    { label: 'Pricing', url: '/printing-prices' }
  ];

  const pricingFaqs = [
    { q: "Is there any minimum order limit for printing at Inchpaper kiosks?", a: "No, there is no minimum order limit. You can print as few as 1 page at ₹2." },
    { q: "What payment methods are supported at Inchpaper kiosks?", a: "We support UPI (GPay, PhonePe, Paytm, BHIM), Credit/Debit cards, and Net Banking." }
  ];

  const serviceSchema = createServiceSchema({
    name: "Document Printing & Binding Rate List",
    description: "Transparent print rates for Black & White, Full Color, A4, A3, Lamination, and Book Binding.",
    url: "https://print.inchpaper.com/printing-prices",
    price: "2.00"
  });

  const breadcrumbSchema = createBreadcrumbListSchema([
    { name: "Home", url: "/" },
    { name: "Pricing", url: "/printing-prices" }
  ]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-between antialiased">
      <SEOHead 
        title="Printing Prices & Rate List | Inchpaper Print Kiosks"
        description="Check transparent document printing rates at Inchpaper kiosks: A4 B&W from ₹2/page, Color from ₹10/page, Spiral Binding, Hardbound Thesis, and Lamination with instant UPI payments."
        keywords="printing prices, xerox rate list, A4 print cost, color printing price, binding rate, inchpaper print rate"
        canonical="https://print.inchpaper.com/printing-prices"
        schemas={[
          serviceSchema, 
          breadcrumbSchema, 
          createFAQPageSchema(pricingFaqs),
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Transparent Print Pricing",
            "speakable": createSpeakable(["h1", "p"])
          }
        ]}
      />

      <Navbar />

      <main className="grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Page Header */}
        <div className="mb-10 text-center max-w-3xl mx-auto space-y-3">
          <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
            Transparent Print Pricing
          </h1>
          <p className="text-slate-600 text-sm sm:text-base">
            No hidden fees or minimum orders. Pay only for what you print with instant UPI, Cards, or Netbanking.
          </p>
        </div>

        {/* Main Pricing Rate Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          
          {/* Black & White Printing */}
          <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200/90 shadow-xs flex flex-col justify-between">
            <div className="space-y-4">
              <span className="px-3 py-1 bg-slate-100 text-slate-800 text-xs font-bold rounded-full">Standard B&W</span>
              <div>
                <span className="text-3xl sm:text-4xl font-extrabold text-slate-900">₹2.00</span>
                <span className="text-xs text-slate-500 font-medium"> / page (A4)</span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Crisp laser black & white text and line drawings on bright white 75 GSM paper.
              </p>
              <ul className="space-y-2 text-xs text-slate-700 pt-2 border-t border-slate-100 font-medium">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>A4 Single Sided: ₹2.00 / page</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>A4 Double Sided Duplex: ₹2.00 / side</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>A3 Large Format B&W: ₹5.00 / page</span>
                </li>
              </ul>
            </div>
            <Link to="/#upload" className="mt-6">
              <Button className="w-full bg-[#7d0909] hover:bg-red-800 text-white rounded-xl font-bold text-xs py-2.5">
                Print B&W Now
              </Button>
            </Link>
          </div>

          {/* Full Color Printing */}
          <div className="bg-white p-6 sm:p-8 rounded-3xl border-2 border-[#7d0909] shadow-sm flex flex-col justify-between relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-[#7d0909] text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl uppercase tracking-wider">
              Popular Choice
            </div>
            <div className="space-y-4">
              <span className="px-3 py-1 bg-red-100 text-[#7d0909] text-xs font-bold rounded-full">Vibrant Color</span>
              <div>
                <span className="text-3xl sm:text-4xl font-extrabold text-[#7d0909]">₹10.00</span>
                <span className="text-xs text-slate-500 font-medium"> / page (A4)</span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                High-definition full color printing for project reports, presentations, and photos.
              </p>
              <ul className="space-y-2 text-xs text-slate-700 pt-2 border-t border-slate-100 font-medium">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>A4 Full Color: ₹10.00 / page</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>A3 Poster Color: ₹20.00 / page</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>Glossy Photo Paper: ₹30.00 / photo</span>
                </li>
              </ul>
            </div>
            <Link to="/#upload" className="mt-6">
              <Button className="w-full bg-[#7d0909] hover:bg-red-800 text-white rounded-xl font-bold text-xs py-2.5">
                Print Color Now
              </Button>
            </Link>
          </div>

          {/* Binding & Finishing */}
          <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200/90 shadow-xs flex flex-col justify-between">
            <div className="space-y-4">
              <span className="px-3 py-1 bg-slate-100 text-slate-800 text-xs font-bold rounded-full">Binding & Lamination</span>
              <div>
                <span className="text-3xl sm:text-4xl font-extrabold text-slate-900">₹30.00</span>
                <span className="text-xs text-slate-500 font-medium"> / document</span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Professional document finishing for college reports, thesis, certificates, and manuals.
              </p>
              <ul className="space-y-2 text-xs text-slate-700 pt-2 border-t border-slate-100 font-medium">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>Spiral & Comb Binding: ₹30 - ₹50</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>Hardbound Golden Embossed Thesis: ₹300 - ₹500</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>Thermal Document Lamination: ₹30 / sheet</span>
                </li>
              </ul>
            </div>
            <Link to="/#upload" className="mt-6">
              <Button className="w-full bg-[#7d0909] hover:bg-red-800 text-white rounded-xl font-bold text-xs py-2.5">
                Order Binding
              </Button>
            </Link>
          </div>

        </div>

        {/* Bulk Rates Note */}
        <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200/90 shadow-xs text-center space-y-3 mb-12">
          <h2 className="text-xl font-bold text-slate-900">Need Bulk Printing or Corporate Discount?</h2>
          <p className="text-xs sm:text-sm text-slate-600 max-w-2xl mx-auto">
            For orders over 500 pages, institutional printing, or regular corporate setups, contact our Bulk Printing desk for special volume discounts.
          </p>
          <div className="pt-2">
            <Link to="/contact-us#bulk-printing">
              <Button variant="outline" className="rounded-xl border-[#7d0909] text-[#7d0909] hover:bg-red-50 font-bold text-xs">
                Contact Bulk Printing Desk →
              </Button>
            </Link>
          </div>
        </div>

      </main>

      <Footer />
    </div>
  );
}
